import { useEffect, useState } from "react";
import { X, Download, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Full-screen image/PDF viewer with a clearly visible close button.
 * The old `<a target="_blank">` pattern broke users out of the installed PWA
 * (no tab bar / no way back) when they tried to view or download a receipt.
 * Downloading now fetches the file as a blob and saves it directly — no new
 * tab or window is ever opened, so the user never leaves the app.
 *
 * Props:
 *  - src: image/PDF URL (used for inline preview)
 *  - alt: text alternative
 *  - onClose: callback when the user dismisses the modal
 *  - downloadHref (optional): URL to fetch for the download
 *  - downloadName (optional): filename to save as
 */
export default function ImageLightbox({ src, alt = "Aperçu", onClose, downloadHref, downloadName, isPdf = false }) {
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleDownload = async () => {
    if (!downloadHref || downloading) return;
    setDownloading(true);
    try {
      const res = await api.get(downloadHref, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName || (isPdf ? "recu.pdf" : "image.jpg");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (_e) {
      // noop — the preview stays visible, nothing opens a stray tab either way
    }
    setDownloading(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="image-lightbox"
    >
      {/* Top toolbar */}
      <div
        className="absolute top-3 right-3 flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        {downloadHref && (
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 py-2 transition disabled:opacity-60"
            data-testid="lightbox-download"
            title="Télécharger"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            <span className="hidden sm:inline">{downloading ? "Téléchargement..." : "Télécharger"}</span>
          </button>
        )}
        <button
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-gray-900 text-xs font-semibold px-3 py-2 shadow-lg hover:bg-gray-100 transition"
          aria-label="Fermer"
          data-testid="lightbox-close"
        >
          <X size={16} /> Fermer
        </button>
      </div>

      <img
        src={src}
        alt={alt}
        className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl ${isPdf ? "hidden" : ""}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
      {isPdf && (
        <iframe
          src={src}
          title={alt}
          className="w-full h-full max-w-4xl rounded-lg shadow-2xl bg-white"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-pdf"
        />
      )}

      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[11px] text-white/70 tracking-wide select-none"
        onClick={(e) => e.stopPropagation()}
      >
        Cliquez à l&apos;extérieur ou appuyez sur Échap pour fermer
      </div>
    </div>
  );
}
